import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BellRing, Download, Gift, Gamepad2, Heart, Maximize2, Sparkles, WifiOff, Zap, Star, Smartphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { PhoneShot } from "@/components/landing/Kit";
import { InstallSteps } from "@/components/InstallSteps";
import { DesktopQr } from "@/components/DesktopQr";
import { AppFriends } from "@/components/AppFriends";
import { LegalLinks } from "@/pages/Legal";
import { canInstall, isStandalone, promptInstall } from "@/lib/push";
import { usePageMeta } from "@/lib/seo";

const PERKS = [
  [Zap, "Blitzschnell", "Mit einem Tipp vom Home-Bildschirm direkt in der Community – ohne Browser suchen."],
  [BellRing, "Push-Nachrichten", "Erfahre sofort von Likes, Nachrichten, Spontan-Aufgaben, Jackpots."],
  [Maximize2, "Vollbild wie eine echte App", "Keine Adressleiste, kein Ablenken – mehr Platz für Feed, Chats und Spiele."],
  [Gift, "Bonus-Punkte", "Für das Installieren und das Einschalten von Push gibt es einmalig Extra-Punkte."],
  [WifiOff, "Immer aktuell", "Kein Store, keine Updates: Neue Funktionen sind sofort für dich da."],
  [Heart, "Kostenlos & sicher", "Keine Anmeldung im App Store nötig, kein Speicherfresser – nur ein paar KB."],
];
const SHOTS = [["/screens/haustier.jpg", "Haustier", -4], ["/screens/spiele.jpg", "Spiele", 0], ["/screens/geschenke.jpg", "Geschenke", 4]];

const Hero = ({ user }) => (
  <section className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
    <div className="space-y-6 min-w-0">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-4">
        <img src="/icon-192.png?v=6" alt="itzPlay App-Symbol" className="w-20 h-20 rounded-[1.4rem] shadow-xl shadow-rose-300/60" data-testid="app-page-icon" />
        <div>
          <p className="font-display text-2xl font-extrabold">itzPlay</p>
          <p className="text-sm text-slate-500">Soziales Netzwerk · Kostenlos</p>
          <p className="flex items-center gap-0.5 text-amber-400 mt-0.5">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-4 h-4 fill-current" />)}<span className="ml-1 text-xs font-bold text-slate-500 truncate">Lieblings-App der Community</span></p>
        </div>
      </motion.div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">Die itzPlay <span className="text-transparent bg-clip-text pink-gradient">App</span> für dein Handy</h1>
      <p className="text-slate-600 text-base md:text-lg max-w-lg">Posten, chatten, spielen, Punkte sammeln und Geschenke abräumen – jetzt mit einem Tipp vom Home-Bildschirm. Für iPhone und Android.</p>
      <div className="flex flex-wrap gap-3">
        {canInstall() && !isStandalone() ? (
          <button onClick={promptInstall} data-testid="app-page-install" className="h-12 px-6 rounded-full pink-gradient text-white font-bold inline-flex items-center gap-2 shadow-lg shadow-rose-200 active:scale-95 transition-transform"><Download className="w-5 h-5" /> Jetzt installieren</button>
        ) : (
          <a href="#installieren" data-testid="app-page-howto" className="h-12 px-6 rounded-full pink-gradient text-white font-bold inline-flex items-center gap-2 shadow-lg shadow-rose-200"><Smartphone className="w-5 h-5" /> So installierst du sie</a>
        )}
        {!user && <Link to="/registrieren" data-testid="app-page-register" className="h-12 px-6 rounded-full border-2 border-rose-200 text-rose-600 font-bold inline-flex items-center">Kostenlos mitmachen</Link>}
      </div>
    </div>
    <div className="relative flex justify-center items-end gap-3 sm:gap-5 pt-6" data-testid="app-page-phones">
      <div className="absolute inset-0 m-auto w-72 h-72 rounded-full bg-gradient-to-tr from-rose-300/50 to-fuchsia-300/50 blur-3xl" aria-hidden />
      {SHOTS.map(([src, alt, tilt], i) => (
        <motion.div key={src} initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 * i, duration: 0.6 }}
          className={`relative ${i === 1 ? "w-[130px] sm:w-[200px] z-10 -mb-2 shrink-0" : "hidden sm:block w-[160px] opacity-90 shrink-0"}`}>
          <PhoneShot src={src} alt={`${alt} in der itzPlay App`} tilt={tilt} testId={`app-page-shot-${i}`} />
        </motion.div>
      ))}
    </div>
  </section>
);

export default function AppPage() {
  const { user } = useAuth();
  const { hash } = useLocation();
  useEffect(() => { if (hash) setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 400); }, [hash]);
  usePageMeta({ title: "Die itzPlay App – kostenlos für iPhone & Android", description: "Hol dir die itzPlay App auf deinen Home-Bildschirm: Push-Nachrichten, Vollbild, Bonus-Punkte. Kostenlos für iPhone und Android." });
  return (
    <div className="min-h-screen noise-bg overflow-x-hidden" data-testid="app-page">
      <header className="max-w-6xl mx-auto px-5 sm:px-8 pt-6 flex items-center justify-between">
        <Logo to={user ? "/feed" : "/"} />
        <Link to={user ? "/feed" : "/"} data-testid="app-page-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-rose-600"><ArrowLeft className="w-4 h-4" /> Zurück</Link>
      </header>
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-16 space-y-20">
        <Hero user={user} />
        {user && <AppFriends />}
        <section className="space-y-8" data-testid="app-page-perks">
          <h2 className="font-display text-2xl sm:text-4xl font-extrabold flex items-center gap-2"><Sparkles className="w-7 h-7 text-rose-500" /> Deine Vorteile</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PERKS.map(([Icon, t, d], i) => (
              <motion.div key={t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="rounded-3xl bg-white border border-pink-100 p-5 space-y-2 hover:-translate-y-1 hover:shadow-xl hover:shadow-rose-100 transition-[transform,box-shadow]" data-testid={`app-page-perk-${i}`}>
                <span className="w-11 h-11 rounded-2xl pink-gradient text-white flex items-center justify-center"><Icon className="w-5 h-5" /></span>
                <p className="font-bold text-lg">{t}</p>
                <p className="text-sm text-slate-600">{d}</p>
              </motion.div>
            ))}
          </div>
        </section>
        <section id="installieren" className="grid lg:grid-cols-2 gap-10 items-center rounded-[2.5rem] bg-white border border-pink-100 p-6 sm:p-12 shadow-xl shadow-rose-100/60 scroll-mt-6" data-testid="app-page-install-section">
          <div className="space-y-4">
            <h2 className="font-display text-2xl sm:text-4xl font-extrabold">In 3 Schritten installiert</h2>
            <DesktopQr />
            <p className="text-slate-600">Öffne diese Seite auf deinem Handy – auf dem iPhone in <b>Safari</b>, auf Android in <b>Chrome</b> – und folge der Anleitung. Danach findest du itzPlay mit dem pinken iP-Symbol auf deinem Home-Bildschirm.</p>
            <p className="flex items-center gap-2 text-sm font-bold text-rose-600"><Gamepad2 className="w-4 h-4" /> Tipp: Schalte danach Push ein – dafür gibt's Bonus-Punkte!</p>
          </div>
          <InstallSteps />
        </section>
      </main>
      <footer className="max-w-6xl mx-auto px-5 sm:px-8 pb-10"><LegalLinks className="justify-center font-semibold text-slate-500" /></footer>
    </div>
  );
}
